import React, { useState, useCallback, useMemo, useRef } from 'react';
import { graphql } from 'react-apollo';
import { useQuery, useApolloClient } from '@apollo/react-hooks';
import compose from 'lodash/flowRight';
import { useLocation } from 'react-router-dom';
import CURRENT_USER from '../../../../GraphQl/Queries/CURRENT_USER';
import CAMPAIGN_ORDER_TRANSACTIONS from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_TRANSACTIONS';
import CAMPAIGN_ORDER_TRANSACTIONS_IMPRESSIONS from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_TRANSACTIONS_IMPRESSIONS';
import CAMPAIGN_ORDER_TRANSACTIONS_DETAILS from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_TRANSACTIONS_DETAILS';
import moment from 'moment-timezone';
import CAMPAIGN_ORDER_TRANSACTIONS_ALL_PAGINATED from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_TRANSACTIONS_ALL_PAGINATED';
import CAMPAIGN_ORDER_TRANSACTION_CREATIVES_PAGINATED from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_TRANSACTION_CREATIVES_PAGINATED';
import CAMPAIGN_ORDER_TRANSACTION_CREATIVES_BY_DATES_PAGINATED from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_TRANSACTION_CREATIVES_BY_DATES_PAGINATED';
import CAMPAIGN_ORDER_TRANSACTION_PUBLISHERS_BY_DATES_PAGINATED from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_TRANSACTION_PUBLISHERS_BY_DATES_PAGINATED';
import CAMPAIGN_ORDER_TRANSACTIONS_BY_CAMPAIGN_BY_DATE from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_TRANSACTIONS_BY_CAMPAIGN_BY_DATE';
import TRANSACTION_CREATIVES_BY_CAMPAIGN from '../../../../GraphQl/Queries/TRANSACTION_CREATIVES_BY_CAMPAIGN';
import TRANSACTION_PUBLISHERS_BY_CAMPAIGN from '../../../../GraphQl/Queries/TRANSACTION_PUBLISHERS_BY_CAMPAIGN';
import { useTimezone } from '../../../../hooks/useTimezone';

// Page size for the recursive transactions-with-events export fetch. Kept modest
// because each row carries its full impressions array.
const EXPORT_PAGE_SIZE = 500;

const TransactionsTabLoader = ({ children, ...props }) => {
  const { campaignOrder } = props;
  const client = useApolloClient();
  const location = useLocation();
  const { timezone: effectiveTimezone = 'UTC' } = useTimezone();

  // State for accumulated paginated data
  const [accumulatedCreativesByDate, setAccumulatedCreativesByDate] = useState([]);
  const [accumulatedPublishersByDate, setAccumulatedPublishersByDate] = useState([]);
  const [isFetchingCreatives, setIsFetchingCreatives] = useState(false);
  const [isFetchingPublishers, setIsFetchingPublishers] = useState(false);

  // State for the full transactions set, accumulated page-by-page so large
  // campaigns don't hang on a single unbounded query. `null` = not yet ready;
  // `{ scopeKey, rows, error }` = fetch complete for that scope (rows may be
  // empty; error = a page failed, so the rows are not a real result). It is
  // only exposed while scopeKey still matches the current where clause, so a
  // set (or failure) from a previous scope is never visible.
  const [allTransactionsExport, setAllTransactionsExport] = useState(null);
  const [isFetchingAllTransactions, setIsFetchingAllTransactions] = useState(false);

  // State for cached campaign byDate data
  const [campaignByDateCache, setCampaignByDateCache] = useState({});
  const [campaignByDateLoading, setCampaignByDateLoading] = useState(false);

  // State for cached per-campaign creatives/publishers data
  const [creativeByCampaignCache, setCreativeByCampaignCache] = useState({});
  const [publisherByCampaignCache, setPublisherByCampaignCache] = useState({});
  const [creativeByCampaignLoading, setCreativeByCampaignLoading] = useState(false);
  const [publisherByCampaignLoading, setPublisherByCampaignLoading] = useState(false);

  // Refs to avoid cache in useCallback deps (prevents new function refs on cache update)
  const campaignByDateCacheRef = useRef(campaignByDateCache);
  campaignByDateCacheRef.current = campaignByDateCache;
  const creativeByCampaignCacheRef = useRef(creativeByCampaignCache);
  creativeByCampaignCacheRef.current = creativeByCampaignCache;
  const publisherByCampaignCacheRef = useRef(publisherByCampaignCache);
  publisherByCampaignCacheRef.current = publisherByCampaignCache;

  // Read pagination and date filter from URL params
  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;
  const pageSize = parseInt(queryParams.get('pageSize')) || 10;
  const startDateParam = queryParams.get('startDate');
  const endDateParam = queryParams.get('endDate');
  const attributionWindow = parseInt(queryParams.get('attributionWindow')) || 30;
  const attributionMethod = queryParams.get('attributionMethod') || 'ALL';
  const campaignIndexParam = queryParams.get('campaignIndex') || 'All';

  // Server-side sort state from URL params. sortField matches the Prisma scalar
  // (orderId, lastImpression, totalImpressions, transactionDate, revenue);
  // sortOrder is 'ascend' | 'descend' to match Ant Design's column sortOrder prop.
  const SORTABLE_FIELDS = new Set([
    'orderId',
    'lastImpression',
    'totalImpressions',
    'transactionDate',
    'revenue',
  ]);
  const sortFieldParam = queryParams.get('sortField');
  const sortOrderParam = queryParams.get('sortOrder');
  const hasUserSort =
    SORTABLE_FIELDS.has(sortFieldParam) &&
    (sortOrderParam === 'ascend' || sortOrderParam === 'descend');
  const orderBy = hasUserSort
    ? `${sortFieldParam}_${sortOrderParam === 'ascend' ? 'ASC' : 'DESC'}`
    : 'transactionDate_ASC';

  // Load the single transaction report via the campaign order's direct 1:1
  // pointer (reportDetails.transaction). This deterministically resolves THE
  // current report instead of reverse-filtering by campaign order id and
  // guessing [0] among orphaned/older reports - the "All" view reads its
  // by-date rollup, creatives, and publishers straight from it.
  const { loading: detailsLoading, data: detailsData } = useQuery(
    CAMPAIGN_ORDER_TRANSACTIONS_DETAILS,
    {
      variables: {
        where: { id: campaignOrder.id },
      },
    }
  );

  const report = detailsData?.campaignOrder?.reportDetails?.transaction;

  // Scope the paginated transactions list to that same report.
  const reportId = report?.id;

  // Wrapper function to recursively fetch all pages of creatives by date
  const fetchAllCreativesByDate = useCallback(
    async (variables) => {
      setIsFetchingCreatives(true);
      setAccumulatedCreativesByDate([]);

      let allData = [];
      let currentSkip = 0;
      let hasNextPage = true;

      try {
        while (hasNextPage) {
          const result = await client.query({
            query: CAMPAIGN_ORDER_TRANSACTION_CREATIVES_BY_DATES_PAGINATED,
            variables: {
              ...variables,
              skip: currentSkip,
            },
            fetchPolicy: 'network-only',
          });

          const edges =
            result.data?.campaignOrderTransactionCreativesByDatesConnection?.edges || [];
          const pageData = edges.map((edge) => edge.node);
          allData = [...allData, ...pageData];

          hasNextPage =
            result.data?.campaignOrderTransactionCreativesByDatesConnection?.pageInfo
              ?.hasNextPage || false;
          currentSkip += variables.first || 1000;
        }

        setAccumulatedCreativesByDate(allData);
      } catch (error) {
        console.error('Error fetching creatives by date:', error);
      } finally {
        setIsFetchingCreatives(false);
      }
    },
    [client]
  );

  // Wrapper function to recursively fetch all pages of publishers by date
  const fetchAllPublishersByDate = useCallback(
    async (variables) => {
      setIsFetchingPublishers(true);
      setAccumulatedPublishersByDate([]);

      let allData = [];
      let currentSkip = 0;
      let hasNextPage = true;

      try {
        while (hasNextPage) {
          const result = await client.query({
            query: CAMPAIGN_ORDER_TRANSACTION_PUBLISHERS_BY_DATES_PAGINATED,
            variables: {
              ...variables,
              skip: currentSkip,
            },
            fetchPolicy: 'network-only',
          });

          const edges =
            result.data?.campaignOrderTransactionPublishersByDatesConnection?.edges || [];
          const pageData = edges.map((edge) => edge.node);
          allData = [...allData, ...pageData];

          hasNextPage =
            result.data?.campaignOrderTransactionPublishersByDatesConnection?.pageInfo
              ?.hasNextPage || false;
          currentSkip += variables.first || 1000;
        }

        setAccumulatedPublishersByDate(allData);
      } catch (error) {
        console.error('Error fetching publishers by date:', error);
      } finally {
        setIsFetchingPublishers(false);
      }
    },
    [client]
  );

  // Lazy fetch function for campaign byDate data
  const fetchCampaignByDate = useCallback(
    async (campaignId) => {
      // Return cached data if available
      if (campaignByDateCacheRef.current[campaignId]) {
        return campaignByDateCacheRef.current[campaignId];
      }

      setCampaignByDateLoading(true);
      try {
        const result = await client.query({
          query: CAMPAIGN_ORDER_TRANSACTIONS_BY_CAMPAIGN_BY_DATE,
          variables: { campaignId },
          fetchPolicy: 'network-only',
        });

        const byDateData = result.data?.transactionByCampaignByDates || [];

        // Cache the result
        setCampaignByDateCache((prev) => ({
          ...prev,
          [campaignId]: byDateData,
        }));

        return byDateData;
      } catch (error) {
        console.error('Error fetching campaign byDate:', error);
        return [];
      } finally {
        setCampaignByDateLoading(false);
      }
    },
    [client]
  );

  // Lazy fetch function for per-campaign creatives data
  const fetchCreativesByCampaign = useCallback(
    async (adProviderId) => {
      // Return cached data if available
      if (creativeByCampaignCacheRef.current[adProviderId]) {
        return creativeByCampaignCacheRef.current[adProviderId];
      }

      setCreativeByCampaignLoading(true);
      try {
        const result = await client.query({
          query: TRANSACTION_CREATIVES_BY_CAMPAIGN,
          variables: { adProviderId },
          fetchPolicy: 'network-only',
        });

        const creatives = result.data?.transactionCreativeByCampaigns || [];

        // Cache the result
        setCreativeByCampaignCache((prev) => ({
          ...prev,
          [adProviderId]: creatives,
        }));

        return creatives;
      } catch (error) {
        console.error('Error fetching creatives by campaign:', error);
        return [];
      } finally {
        setCreativeByCampaignLoading(false);
      }
    },
    [client]
  );

  // Lazy fetch function for per-campaign publishers data
  const fetchPublishersByCampaign = useCallback(
    async (adProviderId) => {
      // Return cached data if available
      if (publisherByCampaignCacheRef.current[adProviderId]) {
        return publisherByCampaignCacheRef.current[adProviderId];
      }

      setPublisherByCampaignLoading(true);
      try {
        const result = await client.query({
          query: TRANSACTION_PUBLISHERS_BY_CAMPAIGN,
          variables: { adProviderId },
          fetchPolicy: 'network-only',
        });

        const publishers = result.data?.transactionPublisherByCampaigns || [];

        // Cache the result
        setPublisherByCampaignCache((prev) => ({
          ...prev,
          [adProviderId]: publishers,
        }));

        return publishers;
      } catch (error) {
        console.error('Error fetching publishers by campaign:', error);
        return [];
      } finally {
        setPublisherByCampaignLoading(false);
      }
    },
    [client]
  );

  // Extract transactionsByCampaign from the scoped report
  const transactionsByCampaignRaw = report?.transactionsByCampaign;
  const transactionsByCampaign = useMemo(
    () => transactionsByCampaignRaw || [],
    [transactionsByCampaignRaw]
  );

  // Build where clause with optional date filter from URL params
  const transactionsWhere = {
    campaignOrderTransactionReport: {
      id: reportId,
    },
  };

  // Add date filter if present in URL
  if (startDateParam && endDateParam) {
    const startMoment = effectiveTimezone === 'UTC'
      ? moment.utc(startDateParam).startOf('day')
      : moment.tz(startDateParam, effectiveTimezone).startOf('day');

    const endMoment = effectiveTimezone === 'UTC'
      ? moment.utc(endDateParam).add(1, 'day').startOf('day')
      : moment.tz(endDateParam, effectiveTimezone).add(1, 'day').startOf('day');

    transactionsWhere.transactionDate_gte = startMoment.toISOString();
    transactionsWhere.transactionDate_lt = endMoment.toISOString();
  }

  // Add attribution window filter
  transactionsWhere.attrWindow_lte = attributionWindow;

  // Add attribution method filter
  if (attributionMethod !== 'ALL') {
    transactionsWhere.attrMethod = attributionMethod;
  }

  // Add campaign filter if specific campaign selected
  if (
    campaignIndexParam !== 'All' &&
    transactionsByCampaign?.[parseInt(campaignIndexParam)]
  ) {
    transactionsWhere.adProviderId =
      transactionsByCampaign[parseInt(campaignIndexParam)].adProviderId;
  }

  // Serialized scope of the full-set fetch (report, date range, attribution
  // filters, campaign). The controller keys its refetch on this.
  const transactionsScopeKey = JSON.stringify(transactionsWhere);

  const { loading: transactionsLoading, data: transactionsData } = useQuery(
    CAMPAIGN_ORDER_TRANSACTIONS,
    {
      variables: {
        where: transactionsWhere,
        first: pageSize,
        skip: (currentPage - 1) * pageSize,
        orderBy,
      },
      skip: !reportId,
    }
  );

  // Keep the latest scope (where/orderBy) in refs so the accumulator can stay a
  // stable callback while always paginating against the current filters.
  const transactionsWhereRef = useRef(transactionsWhere);
  transactionsWhereRef.current = transactionsWhere;
  const orderByRef = useRef(orderBy);
  orderByRef.current = orderBy;
  const transactionsScopeKeyRef = useRef(transactionsScopeKey);
  transactionsScopeKeyRef.current = transactionsScopeKey;
  // Monotonic id of the latest full-set fetch; an older fetch that resolves
  // after a newer one started must not publish its (stale) result.
  const fetchAllSeqRef = useRef(0);

  // Recursively fetch ALL transactions-with-events one page at a time and build
  // the full set on the frontend. Replaces the previous single unbounded query,
  // which hung/timed out on large campaigns. Consumed by both the CSV export and
  // the attribution-filtered byDate/creatives/publishers recompute.
  // Resolves with `{ rows, error }` for the walk this call started, whether or
  // not a newer walk has since taken over the published state, so a caller
  // (the export) always gets the set it asked for.
  const fetchAllTransactions = useCallback(async () => {
    if (!reportId) return { rows: [], error: true };

    // Pin the scope for the whole paginated walk so every page is fetched
    // against the same filters, and tag the result with it.
    const scopeKey = transactionsScopeKeyRef.current;
    const where = transactionsWhereRef.current;
    const orderBy = orderByRef.current;
    const seq = ++fetchAllSeqRef.current;

    setIsFetchingAllTransactions(true);
    setAllTransactionsExport(null);

    let allData = [];
    let currentSkip = 0;
    let hasNextPage = true;
    let error = false;

    try {
      while (hasNextPage) {
        const result = await client.query({
          query: CAMPAIGN_ORDER_TRANSACTIONS_ALL_PAGINATED,
          variables: {
            where,
            orderBy,
            first: EXPORT_PAGE_SIZE,
            skip: currentSkip,
          },
          fetchPolicy: 'network-only',
        });

        const edges = result.data?.campaignOrderTransactionsWithEventsesConnection?.edges || [];
        allData = [...allData, ...edges.map((edge) => edge.node)];

        hasNextPage =
          result.data?.campaignOrderTransactionsWithEventsesConnection?.pageInfo?.hasNextPage ||
          false;
        currentSkip += EXPORT_PAGE_SIZE;
      }
    } catch (e) {
      console.error('Error fetching all transactions:', e);
      // A partial walk is not a result: flag it so consumers show an error
      // rather than treating the rows as a genuinely empty set.
      allData = [];
      error = true;
    }

    // A newer fetch has started since; it owns the published result and
    // loading flag.
    if (seq === fetchAllSeqRef.current) {
      setAllTransactionsExport({ scopeKey, rows: allData, error });
      setIsFetchingAllTransactions(false);
    }

    return { rows: allData, error };
  }, [client, reportId]);

  // Create a wrapper function that uses Apollo Client's query method
  const getImpressions = ({ variables }) => {
    return client.query({
      query: CAMPAIGN_ORDER_TRANSACTIONS_IMPRESSIONS,
      variables,
      fetchPolicy: 'network-only',
    });
  };

  // The published set is only exposed while its scope is still current.
  const currentAllTransactions =
    allTransactionsExport && allTransactionsExport.scopeKey === transactionsScopeKey
      ? allTransactionsExport
      : null;

  return React.cloneElement(children, {
    ...props,
    getImpressions,
    // "All" view data is read straight from the single scoped report.
    byDateData: report?.byDate,
    // By-date data (paginated, loaded on-demand) - use accumulated data if available
    creativesDataByDate: accumulatedCreativesByDate.length > 0 ? accumulatedCreativesByDate : null,
    publishersDataByDate:
      accumulatedPublishersByDate.length > 0 ? accumulatedPublishersByDate : null,
    // Aggregate data (scoped to the report, always loaded with details)
    creativesData: report?.creatives,
    publishersData: report?.publisher,
    transactionsData: transactionsData?.campaignOrderTransactionsWithEventsesConnection,
    details: report,
    transactionsByCampaign,
    campaignOrder,
    pageSize,
    pagination: transactionsData?.full?.aggregate,
    getAllTransactions: fetchAllTransactions,
    allTransactionsData:
      currentAllTransactions && !currentAllTransactions.error
        ? currentAllTransactions.rows
        : null,
    allTransactionsError: Boolean(currentAllTransactions && currentAllTransactions.error),
    transactionsScopeKey,
    // Individual loading states for each component
    byDateLoading: detailsLoading,
    creativesLoading: detailsLoading || isFetchingCreatives,
    publishersLoading: detailsLoading || isFetchingPublishers,
    transactionsLoading: transactionsLoading || (!reportId && detailsLoading),
    detailsLoading,
    allTransactionsLoading: isFetchingAllTransactions,
    // Pass individual by-date loading states as well
    creativesByDateLoading: isFetchingCreatives,
    publishersByDateLoading: isFetchingPublishers,
    // Pass wrapper functions for on-demand loading with pagination
    fetchAllCreativesByDate,
    fetchAllPublishersByDate,
    // Campaign byDate lazy loading
    fetchCampaignByDate,
    campaignByDateLoading,
    // Per-campaign creatives/publishers lazy loading
    fetchCreativesByCampaign,
    fetchPublishersByCampaign,
    creativeByCampaignLoading,
    publisherByCampaignLoading,
  });
};

export default compose(
  graphql(CURRENT_USER, {
    props: ({ data: { currentUser }, ownProps }) => {
      return ownProps.currentUser || { currentUser };
    },
  })
)(TransactionsTabLoader);
